import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Breadcrumbs, Typography, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Container, MainContainer } from '../../../../components';
import { Menu } from '../Menu/Menu';
import { useToaster } from '../../../../providers/ToasterProvider';
import { createVenue, getVenueById, updateVenueById } from '../../../../services/venueMasterService';
import RadiusButton from '../../../../components/RadiusButton';
import { ProjectContext } from '../../../../providers/projectProvider';

import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import BACK from '../../../../assets/images/back.svg';

import '../UserDetails/UserDetails.scss';

const venueSchema = z.object({
    venueName: z.string().trim().min(1, { message: 'Venue name is required.' }),
});

// Add or edit one venue. The name must match the "ExamHallName" used in candidate uploads.
export const VenueMasterDetails = () => {

    const location = useLocation();
    const navigate = useNavigate();
    const { addNotification } = useToaster();
    const { projectDetails } = useContext(ProjectContext);
    const venueId = location?.state?.venueId ?? null;
    const editMode = Boolean(venueId);
    const [isLoading, setIsLoading] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        trigger,
        reset,
    } = useForm({
        resolver: zodResolver(venueSchema),
    });

    useEffect(() => {
        if (!venueId) return;
        const fetchVenue = async () => {
            try {
                const res = await getVenueById(venueId);
                if (res.code === 'OK') {
                    reset({ venueName: res.data.venueName });
                }
            } catch (error) {
                console.log('There is an error while fetching venue', error);
            }
        }
        fetchVenue();
    }, [venueId, reset]);

    const onGoBack = () => {
        navigate(location.pathname.replace('/venue-master-details', ''));
    }

    const submitForm = async (values) => {
        const body = { venueName: values.venueName.trim() };
        setIsLoading(true);
        try {
            const res = editMode ? await updateVenueById(venueId, body) : await createVenue(body);
            if (res?.code === 'OK') {
                addNotification(editMode ? 'Venue updated successfully.' : 'Venue added successfully.', 'success');
                onGoBack();
            }
        } catch (error) {
            // e.g. "A venue with this name already exists."
            addNotification(error?.response?.data?.message ?? 'Venue could not be saved.', 'danger');
            console.log('There is an error while saving venue', error);
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <MainContainer>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography color="text.primary" sx={{
                        fontWeight: 400,
                        textTransform: 'capitalize'
                    }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>

                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>
                        {editMode ? 'Edit Venue' : 'Add Venue'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>
            <Container sx={{
                display: 'flex',
                flexDirection: 'column',
            }}>

                <Typography component='h5' variant='h5' sx={{ mb: 3 }}>
                    {editMode ? 'Edit Venue' : 'Add Venue'}
                </Typography>

                <Box sx={{
                    bgcolor: 'background.base',
                    borderRadius: 6,
                    p: 4
                }} className='create-user-form-wrapper'>
                    <Box component='form' autoComplete='off' onSubmit={handleSubmit(submitForm)}>
                        <div className="user-form-details">
                            <TextField
                                id='venueName'
                                name='venueName'
                                className='form-field'
                                label={
                                    <>
                                        <Box component='span'>Venue Name</Box>
                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                    </>
                                }
                                variant='outlined'
                                {...register('venueName')}
                                error={Boolean(errors.venueName)}
                                helperText={errors.venueName?.message ?? 'Must match the ExamHallName column value in your candidate upload sheet (case-insensitive).'}
                                onBlur={() => trigger('venueName')}
                                InputLabelProps={editMode ? { shrink: true } : {}}
                            />
                        </div>

                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            mt: 3
                        }}>
                            <RadiusButton sx={{
                                textTransform: 'uppercase',
                                fontWeight: 500,
                            }} color='grey'
                                variant='contained'
                                size='large'
                                onClick={onGoBack}
                                startIcon={<img src={BACK} alt='' />}>
                                Back
                            </RadiusButton>
                            <LoadingButton sx={{
                                textTransform: 'uppercase',
                                borderRadius: 40,
                            }} color='primary'
                                type="submit"
                                variant='contained'
                                size='large'
                                loading={isLoading}
                            >
                                Submit
                            </LoadingButton>
                        </Box>
                    </Box>
                </Box>

            </Container>
        </MainContainer>
    );
}
